import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Bell, ClipboardList, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuthStore } from '@/stores/auth';
import { useOnboardingStore } from '@/stores/onboarding';
import { useNotificationStore } from '@/stores/notifications';
import type { Notification } from '@/types';
import { cn } from '@/lib/utils';
import { navIconButtonClass } from '@/components/layout/navIconButton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';

function openOnboardingModal() {
  window.dispatchEvent(new CustomEvent('leftclick:onboarding-open-modal'));
}

export function GuidanceHubMenu({ onNavigate }: { onNavigate: (path: string) => void }) {
  const user = useAuthStore((s) => s.user);
  const payload = useOnboardingStore((s) => s.payload);
  const onboardingLoading = useOnboardingStore((s) => s.loading);
  const fetchOnboarding = useOnboardingStore((s) => s.fetch);
  const resetOnboarding = useOnboardingStore((s) => s.reset);

  const notifications = useNotificationStore((s) => s.notifications);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const fetchNotifications = useNotificationStore((s) => s.fetchNotifications);
  const fetchUnreadCount = useNotificationStore((s) => s.fetchUnreadCount);
  const markAsRead = useNotificationStore((s) => s.markAsRead);

  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user?.id) {
      resetOnboarding();
      return;
    }
    void fetchOnboarding();
  }, [user?.id, fetchOnboarding, resetOnboarding]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    void fetchUnreadCount();
    void fetchNotifications().catch(() => toast.error('Benachrichtigungen konnten nicht geladen werden.'));
  }, [open, fetchNotifications, fetchUnreadCount]);

  const completion = useMemo(() => {
    const tasks = payload?.tasks ?? [];
    const completed = tasks.filter((t) => Boolean(t.progress?.completed_at)).length;
    return { completed, total: tasks.length };
  }, [payload]);

  const nextTasks = useMemo(() => {
    const tasks = payload?.tasks ?? [];
    return tasks.filter((t) => !t.progress?.completed_at).slice(0, 4);
  }, [payload]);

  const progressPct =
    completion.total > 0 ? Math.round((completion.completed / completion.total) * 100) : 0;

  const recentNotes = useMemo(() => {
    const list = [...notifications].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    return list.slice(0, 6);
  }, [notifications]);

  const incompleteTasks = useMemo(() => {
    const tasks = payload?.tasks ?? [];
    return tasks.filter((t) => !t.progress?.completed_at).length;
  }, [payload]);

  const attentionCount = incompleteTasks + unreadCount;

  const openNote = async (n: Notification) => {
    try {
      if (!n.is_read) await markAsRead(n.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Benachrichtigung konnte nicht als gelesen markiert werden.');
      return;
    }
    if (n.link) {
      const path = n.link.startsWith('/') ? n.link : `/${n.link}`;
      onNavigate(path);
    } else {
      onNavigate('/notifications');
    }
    setOpen(false);
  };

  if (!user) return null;

  return (
    <div ref={wrapRef} className="relative">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-label="Nächste Schritte und Benachrichtigungen"
            aria-expanded={open}
            aria-haspopup="dialog"
            className={cn(
              navIconButtonClass,
              'relative',
              open && 'bg-card-hover text-foreground dark:text-muted'
            )}
          >
            <ClipboardList size={18} className="shrink-0" aria-hidden />
            {attentionCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                {attentionCount > 99 ? '99+' : attentionCount}
              </span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Nächste Schritte und Benachrichtigungen</TooltipContent>
      </Tooltip>

      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-1 w-[min(100vw-2rem,24rem)] max-h-[min(72vh,28rem)] overflow-y-auto rounded-lg border border-border bg-card py-3 shadow-xl"
          role="dialog"
          aria-label="Nächste Schritte und Benachrichtigungen"
        >
          <div className="border-b border-border px-3 pb-3">
            <p className="text-xs font-semibold text-muted">
              Nächste Schritte
            </p>
            {onboardingLoading && !payload ? (
              <div className="mt-3 flex items-center gap-2 text-sm text-muted">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Laden…
              </div>
            ) : !payload ? (
              <p className="mt-2 text-sm text-muted">
                Kein aktives Onboarding-Programm. Nutzen Sie die Onboarding-Seite oder den Companion bei Fragen.
              </p>
            ) : (
              <>
                <p className="mt-1 text-sm font-medium text-foreground">{payload.template.name}</p>
                <div className="mt-2 space-y-1.5">
                  <div className="flex items-center justify-between text-xs text-muted">
                    <span>
                      {completion.completed}/{completion.total} erledigt
                    </span>
                    <span className="tabular-nums">{progressPct}%</span>
                  </div>
                  <Progress value={progressPct} className="h-1.5" />
                </div>
                {nextTasks.length > 0 ? (
                  <ul className="mt-3 space-y-1">
                    {nextTasks.map((task) => (
                      <li key={task.id}>
                        <div className="rounded-md border border-border px-2 py-1.5">
                          <p className="text-sm font-medium text-foreground">{task.title}</p>
                          {task.action_path && (
                            <button
                              type="button"
                              className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline dark:text-muted"
                              onClick={() => {
                                const p = task.action_path!.startsWith('/') ? task.action_path! : `/${task.action_path}`;
                                onNavigate(p);
                                setOpen(false);
                              }}
                            >
                              Öffnen <ArrowRight className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-sm text-muted">Alle Punkte erledigt.</p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button type="button" size="sm" className="h-8 text-xs" onClick={() => { openOnboardingModal(); setOpen(false); }}>
                    Checkliste
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => { onNavigate('/onboarding'); setOpen(false); }}>
                    Onboarding-Seite
                  </Button>
                </div>
              </>
            )}
          </div>

          <div className="px-3 pt-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-muted">
                Benachrichtigungen
              </p>
              {unreadCount > 0 && (
                <span className="rounded-full bg-danger px-1.5 py-0.5 text-[10px] font-bold text-danger-foreground">
                  {unreadCount} neu
                </span>
              )}
            </div>
            {recentNotes.length === 0 ? (
              <p className="mt-2 text-sm text-muted">Keine Einträge.</p>
            ) : (
              <ul className="mt-2 space-y-1">
                {recentNotes.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => void openNote(n)}
                      className={cn(
                        'flex w-full flex-col rounded-md px-2 py-2 text-left text-sm hover:bg-background dark:hover:bg-card-hover/80',
                        !n.is_read && 'bg-card-hover dark:bg-card'
                      )}
                    >
                      <span className="font-medium text-foreground">{n.title}</span>
                      {n.message && (
                        <span className="mt-0.5 line-clamp-2 text-xs text-muted">{n.message}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mt-2 h-8 w-full justify-center gap-1.5 text-xs"
              onClick={() => {
                onNavigate('/notifications');
                setOpen(false);
              }}
            >
              <Bell className="h-3.5 w-3.5" />
              Alle Benachrichtigungen
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
